import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { AlertTriangle, ArrowRight, MapPin, Heart, Truck, HandHeart, Filter } from "lucide-react";
import Countdown from "../components/Countdown";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { daysLeft } from "../lib/format";

export default function AtRiskDashboard() {
    const [params, setParams] = useSearchParams();
    const [dogs, setDogs] = useState([]);
    const [q, setQ] = useState("");
    const [country, setCountry] = useState(params.get("country") || "");
    const [region, setRegion] = useState("");
    const [source, setSource] = useState("");
    const [size, setSize] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/dogs/at-risk").then((r) => { setDogs(r.data); setLoading(false); });
    }, []);

    const countries = useMemo(() => Array.from(new Set(dogs.map((d) => d.country).filter(Boolean))).sort(), [dogs]);
    const regions = useMemo(() => Array.from(new Set(dogs.map((d) => d.region).filter(Boolean))).sort(), [dogs]);

    const filtered = useMemo(() => dogs.filter((d) => {
        if (q && !`${d.name} ${d.breed} ${d.location}`.toLowerCase().includes(q.toLowerCase())) return false;
        if (country && d.country !== country) return false;
        if (region && d.region !== region) return false;
        if (size && d.size !== size) return false;
        if (source && d.rescue_source !== source) return false;
        return true;
    }), [dogs, q, country, region, size, source]);

    const critical = filtered.filter((d) => daysLeft(d.euthanasia_date) <= 2);
    const others = filtered.filter((d) => daysLeft(d.euthanasia_date) > 2);

    return (
        <div data-testid="atrisk-page">
            {/* Header block - Swiss high-contrast */}
            <div className="border-b border-[var(--rl-border)] bg-[var(--rl-surface)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                    <div className="grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-8">
                            <div className="flex items-center gap-2 text-[var(--rl-urgent)] mb-4">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="eyebrow text-[var(--rl-urgent)]">The At-Risk Dashboard</span>
                            </div>
                            <h1 className="font-serif text-4xl lg:text-6xl tracking-tight leading-[1.02]">
                                {dogs.length} dogs worldwide are running out of time.
                            </h1>
                            <p className="mt-4 text-[var(--rl-text-2)] max-w-2xl">
                                Every dog listed here has a scheduled euthanasia date — from municipal pounds in Delhi and Seoul to county shelters in Texas. A single pledge, foster commitment, or transport offer can save their life across any border.
                            </p>
                        </div>
                        <div className="lg:col-span-4 grid grid-cols-3 gap-4 text-left">
                            <div className="border border-[var(--rl-border)] p-4">
                                <div className="font-serif text-3xl text-[var(--rl-urgent)]">{critical.length}</div>
                                <div className="eyebrow mt-1">Within 48h</div>
                            </div>
                            <div className="border border-[var(--rl-border)] p-4">
                                <div className="font-serif text-3xl">{dogs.length}</div>
                                <div className="eyebrow mt-1">Total at risk</div>
                            </div>
                            <div className="border border-[var(--rl-border)] p-4">
                                <div className="font-serif text-3xl">{countries.length}</div>
                                <div className="eyebrow mt-1">Countries</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="border-b border-[var(--rl-border)] bg-[var(--rl-surface)] sticky top-16 z-30">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-3">
                    <Filter className="w-4 h-4 text-[var(--rl-text-3)]" />
                    <Input placeholder="Search name, breed, location…" value={q} onChange={(e) => setQ(e.target.value)}
                           className="max-w-xs border-[var(--rl-border)]" data-testid="atrisk-search" />
                    <Select value={region} onValueChange={(v) => setRegion(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-40 border-[var(--rl-border)]" data-testid="atrisk-region-filter"><SelectValue placeholder="Any region" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any region</SelectItem>
                            {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={country} onValueChange={(v) => { const val = v === "all" ? "" : v; setCountry(val); if (val) setParams({ country: val }); else setParams({}); }}>
                        <SelectTrigger className="w-44 border-[var(--rl-border)]" data-testid="atrisk-country-filter"><SelectValue placeholder="Any country" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any country</SelectItem>
                            {countries.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={size} onValueChange={(v) => setSize(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-36 border-[var(--rl-border)]" data-testid="atrisk-size-filter"><SelectValue placeholder="Any size" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any size</SelectItem>
                            <SelectItem value="small">Small</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="large">Large</SelectItem>
                            <SelectItem value="xl">XL</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="text-xs text-[var(--rl-text-3)] ml-auto">Showing {filtered.length} of {dogs.length}</div>
                </div>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-3 flex flex-wrap gap-2 items-center" data-testid="source-filter-row">
                    <span className="eyebrow text-[var(--rl-text-3)]">Rescue source:</span>
                    {[
                        { k: "", label: "All" },
                        { k: "meat_trade", label: "Meat Trade" },
                        { k: "street", label: "Street" },
                        { k: "surrender", label: "Surrender" },
                        { k: "shelter_overflow", label: "Overflow" },
                    ].map((s) => (
                        <button key={s.label} onClick={() => setSource(s.k)}
                                data-testid={`source-chip-${s.k || "all"}`}
                                className={`px-3 py-1 rounded-full text-xs border ${source === s.k ? (s.k === "meat_trade" ? "bg-[#4a1a1a] text-white border-[#4a1a1a]" : "bg-[var(--rl-text)] text-white border-[var(--rl-text)]") : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Critical grid */}
            {loading ? (
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 text-center text-[var(--rl-text-2)]">Loading dogs at risk…</div>
            ) : (
                <>
                    {critical.length > 0 && (
                        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="critical-section">
                            <div className="flex items-baseline gap-3 mb-6">
                                <h2 className="font-serif text-3xl">Within 48 hours</h2>
                                <span className="eyebrow text-[var(--rl-urgent)]">Critical</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--rl-border)] border border-[var(--rl-border)]">
                                {critical.map((d) => <AtRiskRow key={d.id} dog={d} />)}
                            </div>
                        </section>
                    )}
                    {others.length > 0 && (
                        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                            <h2 className="font-serif text-3xl mb-6">This week</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--rl-border)] border border-[var(--rl-border)]">
                                {others.map((d) => <AtRiskRow key={d.id} dog={d} />)}
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}

function AtRiskRow({ dog }) {
    const { user } = useAuth();
    const d = daysLeft(dog.euthanasia_date);
    const critical = d <= 1;
    const meat = dog.rescue_source === "meat_trade";

    return (
        <div className={`bg-[var(--rl-surface)] p-5 lg:p-6 grid grid-cols-[100px_1fr_auto] md:grid-cols-[130px_1fr_auto] gap-5 items-center ${critical ? "border-l-4 border-[var(--rl-urgent)]" : ""}`}
             data-testid={`atrisk-row-${dog.id}`}>
            <Link to={`/dogs/${dog.id}`} className="block aspect-square rounded-lg overflow-hidden bg-[var(--rl-cream)] border border-[var(--rl-border)] relative">
                <img src={dog.photo_url} alt={dog.name} className="w-full h-full object-cover" loading="lazy" />
                {meat && (
                    <div className="absolute top-1.5 left-1.5 bg-[#4a1a1a] text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold" data-testid="meat-trade-badge">
                        Meat Trade
                    </div>
                )}
            </Link>
            <div>
                <div className="flex items-center gap-3 mb-1">
                    <Countdown isoDate={dog.euthanasia_date} />
                    <span className="eyebrow text-[var(--rl-text-3)]">{dog.shelter_name}</span>
                </div>
                <Link to={`/dogs/${dog.id}`} className="font-serif text-2xl hover:text-[var(--rl-primary)]">{dog.name}</Link>
                <div className="text-sm text-[var(--rl-text-2)]">{dog.breed} · {dog.age} · {dog.size}</div>
                <div className="flex items-center gap-1 text-xs text-[var(--rl-text-3)] mt-1"><MapPin className="w-3 h-3" /> {dog.location}</div>
                <p className="text-sm text-[var(--rl-text-2)] mt-2 line-clamp-2 max-w-2xl">{dog.description}</p>
            </div>
            <div className="flex flex-col gap-2 min-w-[150px]">
                <PledgeAction dog={dog} type="rescue" label="Rescue Pledge" icon={<Heart className="w-4 h-4" />} primary />
                <PledgeAction dog={dog} type="foster" label="Foster" icon={<HandHeart className="w-4 h-4" />} />
                <PledgeAction dog={dog} type="transport" label="Transport" icon={<Truck className="w-4 h-4" />} />
                <Link to={`/dogs/${dog.id}`} className="text-xs text-[var(--rl-text-3)] flex items-center gap-1 mt-1 hover:text-[var(--rl-primary)]">
                    View profile <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
        </div>
    );
}

export function PledgeAction({ dog, type, label, icon, primary }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);

    async function submit() {
        if (!user) { toast.error("Please sign in to pledge"); setOpen(false); return; }
        setSending(true);
        try {
            const payload = { dog_id: dog.id, type, message: msg, amount: amount ? parseFloat(amount) : null };
            await api.post("/pledges", payload);
            toast.success(`${label} recorded for ${dog.name}. Thank you.`);
            setOpen(false); setMsg(""); setAmount("");
        } catch (e) {
            toast.error("Could not record pledge. Try again.");
        } finally { setSending(false); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm"
                        className={primary ? "btn-urgent rounded-md h-9 justify-start" : "rounded-md h-9 justify-start bg-[var(--rl-cream)] text-[var(--rl-text)] hover:bg-[var(--rl-sage)]"}
                        data-testid={`pledge-${type}-${dog.id}`}>
                    <span className="mr-2">{icon}</span>{label}
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--rl-surface)]" data-testid={`pledge-dialog-${type}`}>
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        {label} for {dog.name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-[var(--rl-text-2)]">Your pledge signals a rescue coordinator. They will contact you to confirm details.</div>
                    {type !== "foster" && type !== "transport" && (
                        <div>
                            <label className="eyebrow">Financial pledge (optional)</label>
                            <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 50" className="mt-2 border-[var(--rl-border)]" data-testid="pledge-amount" />
                        </div>
                    )}
                    <div>
                        <label className="eyebrow">Message (optional)</label>
                        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="How can you help?" className="mt-2 border-[var(--rl-border)]" data-testid="pledge-message" />
                    </div>
                    <Button className="btn-primary w-full rounded-full h-11" disabled={sending} onClick={submit} data-testid="pledge-submit">
                        {sending ? "Recording…" : `Confirm ${label}`}
                    </Button>
                    {!user && <div className="text-xs text-center text-[var(--rl-text-3)]">You'll need to <Link to="/auth" className="underline">sign in</Link> first.</div>}
                </div>
            </DialogContent>
        </Dialog>
    );
}
